export type RuntimeLocationSnapshot = {
  protocol: string;
  hostname: string;
  port: string;
  host: string;
};

export type PublicRuntimeEnv = Partial<
  Record<
    | 'NEXT_PUBLIC_API_URL'
    | 'NEXT_PUBLIC_WS_URL'
    | 'NEXT_PUBLIC_WS_ENABLED'
    | 'NEXT_PUBLIC_COLYSEUS_URL'
    | 'NEXT_PUBLIC_COLYSEUS_ENABLED'
    | 'NEXT_PUBLIC_P2P_ICE_URL'
    | 'NEXT_PUBLIC_P2P_SIGNAL_BASE_URL'
    | 'NODE_ENV',
    string
  >
>;

export type RealtimeMode = 'polling' | 'socket.io' | 'colyseus';

export type PublicRuntimeConfig = {
  apiUrl: string;
  websocketUrl: string;
  websocketEnabled: boolean;
  colyseusUrl: string;
  colyseusEnabled: boolean;
  realtimeMode: RealtimeMode;
  debug: boolean;
};

const LOCAL_FRONTEND_PORT = '3000';
const LOCAL_API_PORT = '8080';
const LOCAL_WS_PORT = '3001';
const LOCAL_COLYSEUS_PORT = '3002';
const LOCAL_HOSTNAMES = new Set(['localhost', '127.0.0.1']);

function trimTrailingSlash(value: string | undefined): string {
  return String(value || '').trim().replace(/\/+$/, '');
}

function parseExplicitBoolean(value: string | undefined): boolean | null {
  if (typeof value !== 'string') return null;
  const normalized = value.trim().toLowerCase();
  if (normalized === '') return null;
  if (['1', 'true', 'yes', 'on'].includes(normalized)) return true;
  if (['0', 'false', 'no', 'off'].includes(normalized)) return false;
  return null;
}

function isLocalFrontendDev(location: RuntimeLocationSnapshot | null | undefined): boolean {
  if (!location) return false;
  return LOCAL_HOSTNAMES.has(location.hostname) && location.port === LOCAL_FRONTEND_PORT;
}

function resolveApiUrl(env: PublicRuntimeEnv, location: RuntimeLocationSnapshot | null | undefined): string {
  const explicitUrl = trimTrailingSlash(env.NEXT_PUBLIC_API_URL);
  if (explicitUrl) return explicitUrl;

  if (isLocalFrontendDev(location)) {
    return `${location?.protocol}//${location?.hostname}:${LOCAL_API_PORT}`;
  }

  if (location) {
    return `${location.protocol}//${location.host}`;
  }

  return `http://localhost:${LOCAL_API_PORT}`;
}

function resolveWsUrl(env: PublicRuntimeEnv, location: RuntimeLocationSnapshot | null | undefined): string {
  const explicitUrl = trimTrailingSlash(env.NEXT_PUBLIC_WS_URL);
  if (explicitUrl) return explicitUrl;

  if (isLocalFrontendDev(location)) {
    const wsProtocol = location?.protocol === 'https:' ? 'wss:' : 'ws:';
    return `${wsProtocol}//${location?.hostname}:${LOCAL_WS_PORT}`;
  }

  return '';
}

function resolveColyseusUrl(env: PublicRuntimeEnv, location: RuntimeLocationSnapshot | null | undefined): string {
  const explicitUrl = trimTrailingSlash(env.NEXT_PUBLIC_COLYSEUS_URL);
  if (explicitUrl) return explicitUrl;

  if (isLocalFrontendDev(location)) {
    const wsProtocol = location?.protocol === 'https:' ? 'wss:' : 'ws:';
    return `${wsProtocol}//${location?.hostname}:${LOCAL_COLYSEUS_PORT}`;
  }

  if (location && location.protocol === 'https:') {
    return `wss://${location.host}/colyseus`;
  }

  return '';
}

export function resolvePublicRuntimeConfig(
  env: PublicRuntimeEnv,
  location: RuntimeLocationSnapshot | null | undefined
): PublicRuntimeConfig {
  const websocketUrl = resolveWsUrl(env, location);
  const colyseusUrl = resolveColyseusUrl(env, location);
  const explicitWsEnabled = parseExplicitBoolean(env.NEXT_PUBLIC_WS_ENABLED);
  const explicitColyseusEnabled = parseExplicitBoolean(env.NEXT_PUBLIC_COLYSEUS_ENABLED);
  const websocketEnabled = explicitWsEnabled === null ? websocketUrl !== '' : explicitWsEnabled && websocketUrl !== '';
  const colyseusEnabled = explicitColyseusEnabled === null ? false : explicitColyseusEnabled && colyseusUrl !== '';

  return {
    apiUrl: resolveApiUrl(env, location),
    websocketUrl,
    websocketEnabled,
    colyseusUrl,
    colyseusEnabled,
    realtimeMode: colyseusEnabled ? 'colyseus' : websocketEnabled ? 'socket.io' : 'polling',
    debug: env.NODE_ENV !== 'production',
  };
}

function readWindowLocation(): RuntimeLocationSnapshot | null {
  if (typeof window === 'undefined') return null;
  const { protocol, hostname, port, host } = window.location;
  return { protocol, hostname, port, host };
}

const publicRuntimeEnv: PublicRuntimeEnv = {
  NEXT_PUBLIC_API_URL: process.env.NEXT_PUBLIC_API_URL,
  NEXT_PUBLIC_WS_URL: process.env.NEXT_PUBLIC_WS_URL,
  NEXT_PUBLIC_WS_ENABLED: process.env.NEXT_PUBLIC_WS_ENABLED,
  NEXT_PUBLIC_COLYSEUS_URL: process.env.NEXT_PUBLIC_COLYSEUS_URL,
  NEXT_PUBLIC_COLYSEUS_ENABLED: process.env.NEXT_PUBLIC_COLYSEUS_ENABLED,
  NEXT_PUBLIC_P2P_ICE_URL: process.env.NEXT_PUBLIC_P2P_ICE_URL,
  NEXT_PUBLIC_P2P_SIGNAL_BASE_URL: process.env.NEXT_PUBLIC_P2P_SIGNAL_BASE_URL,
  NODE_ENV: process.env.NODE_ENV,
};

export const publicRuntimeConfig = resolvePublicRuntimeConfig(publicRuntimeEnv, readWindowLocation());
